import { IObservable, ISubject, BehaviorSubject } from "@app/utils/observable";
import { SearchFilterModel, SearchFilterAllowanceTypeModel } from "@app/models/search/filter";
import { ModalContainerComponent } from '../modal/container';
import { MemberPickerModalComponent } from '../member/picker-modal';
import { MemberCardCheckboxComponent } from '../member/card-checkbox';
import { guid } from '@app/utils/guid';
import { AllowanceTypeAccordionItemComponent, AllowanceTypeAccordionItemExpenditureTypeChangeArgs } from '../allowance-type/accordion-item';
import { AllowanceTypePickerModalComponent } from '../allowance-type/picker-modal';
import { toDictionary } from '@app/utils/to-dictionary';
import { NumberCardComponent } from '@app/components/common/number-card';
import { ICheckboxChangeArgs } from '@app/interfaces/checkbox';
import { fail } from 'assert';

export class SearchFilterComponent {
    private readonly searchEventSubject: ISubject<SearchFilterComponent, SearchEventArgs>;

	private toMonthElement!: HTMLSelectElement;
	private fromMonthElement!: HTMLSelectElement;
    private financialYearElement!: HTMLSelectElement;
    private searchMembersButton!: HTMLButtonElement;
    private searchAllowanceTypesButton!: HTMLButtonElement;
    private allowanceTypeCardContainer!: HTMLElement;
    private searchWarningContainer!: HTMLElement;

    private membersPickerModal: MemberPickerModalComponent | null = null;
    private allowanceTypePickerModal!: AllowanceTypePickerModalComponent;

    private lastSearchModel: SearchFilterModel | null = null;

    private memberCardsMap: { [key: number]: MemberCardCheckboxComponent; } = {};
    private memberCardIds: number[] = []; // Array of IDs kept alongside map to preserve ordering
    private get memberCards() {
        return this.memberCardIds.map(id => this.memberCardsMap[id]);
    }

    private selectedAllMembersCard!: NumberCardComponent;
    private selectedAllAllowanceTypesCard!: NumberCardComponent;
    
    private allowanceTypeCardsMap: { [key: number]: NumberCardComponent; } = {};
    private allowanceTypeCardIds: number[] = []; // Array of IDs kept alongside map to preserve ordering
    private get allowanceTypeCards() {
        return this.allowanceTypeCardIds.map(id => this.allowanceTypeCardsMap[id]);
    }

    public get onSearch$(): IObservable<SearchFilterComponent, SearchEventArgs> {
        return this.searchEventSubject;
    }

    constructor(private formElement: HTMLFormElement, private modalContainer: ModalContainerComponent) {
        this.searchEventSubject = new BehaviorSubject;

        this.bindFunctions();

        this.selectElements();

        this.attachFormEventListeners();
        this.attachMembersEventListeners();
        this.attachAllowanceTypesEventListeners();
        this.attachDateSelectEventListeners();

        this.buildSelectedAllMembersCard();
        this.buildSelectedAllAllowanceTypesCard();

        this.buildAllMemberCardCheckboxes();

        this.buildAllowanceTypePickerModal();
        this.buildAllowanceTypeCards();
    }

    //#region Constructor methods
    private bindFunctions() {
        this.onAllowanceTypeChange = this.onAllowanceTypeChange.bind(this);
        this.onExpenditureTypeChange = this.onExpenditureTypeChange.bind(this);
    }

    private selectElements() {
		this.fromMonthElement = this.formElement.querySelector(`[name=FromMonth]`) as HTMLSelectElement;
		this.toMonthElement = this.formElement.querySelector(`[name=ToMonth]`) as HTMLSelectElement;
        this.financialYearElement = this.formElement.querySelector(`[name=FinancialYear]`) as HTMLSelectElement;
        this.searchMembersButton = this.formElement.querySelector(".search-filter__search-members-button") as HTMLButtonElement;
        this.searchAllowanceTypesButton = this.formElement.querySelector(".search-filter__search-allowance-types-button") as HTMLButtonElement;
        this.allowanceTypeCardContainer = this.formElement.querySelector(".search-filter__allowance-types") as HTMLElement;
        this.searchWarningContainer = document.querySelector(`.search-results-warning`) as HTMLElement;
    }

    private attachFormEventListeners() {
        this.formElement.addEventListener("submit", ev => {
            ev.preventDefault();

            if (this.isFormValid()) {
                const serialized = this.serialize();
                this.searchEventSubject.emit(this, { searchFilter: serialized });
                this.lastSearchModel = serialized;
                this.updateSearchWarning();
            }
        });

        this.formElement.addEventListener("reset", ev => {
            for (const card of this.memberCards) {
                card.reset();
            }

            this.updateSelectedAllMemberCardState();

            this.allowanceTypePickerModal.reset();
            this.financialYearElement.selectedIndex = 0;
            this.fromMonthElement.selectedIndex = 0;
            this.toMonthElement.selectedIndex = this.toMonthElement.length - 1;

            this.updateSearchWarning();
        })
    }

    private attachDateSelectEventListeners() {
        this.financialYearElement.addEventListener("change", ev => {
            this.updateSearchWarning();
        });

        this.fromMonthElement.addEventListener("change", ev => {
            this.updateSearchWarning();
        });

        this.toMonthElement.addEventListener("change", ev => {
            this.updateSearchWarning();
        });
    }

    private attachMembersEventListeners() {
        this.searchMembersButton.addEventListener("click", ev => {
            ev.preventDefault();
            ev.stopPropagation();

            this.modalContainer.push(this.getOrBuildMemberPickerModal());
        })
    }

    private attachAllowanceTypesEventListeners() {
        this.searchAllowanceTypesButton.addEventListener("click", ev => {
            ev.preventDefault();
            ev.stopPropagation();

            this.modalContainer.push(this.allowanceTypePickerModal);
        })
    }
	
    //#endregion

    //#region Member picker modal
    private buildAllMemberCardCheckboxes() {
        const $cards = $(".search-filter__members .member-card-checkbox", this.formElement).toArray();
        
        const cards = $cards.map(card => new MemberCardCheckboxComponent(card));

        this.memberCardIds = cards.map(c => c.id);
        this.memberCardsMap = toDictionary(cards, c => c.id);

        for (const card of cards) {
            card.outlineOnChecked = false;
            card.disabled = false;

            card.onChange$.subscribe((_, ev) => {
                $(card.element).toggleClass("search-filter__member--hidden", !ev.checked);

                if (this.membersPickerModal) {
                    this.membersPickerModal.receiveMemberChangeEvent(ev);
                }
            });
        }

        this.updateSelectedAllMemberCardState();
    }

    private getOrBuildMemberPickerModal() {
        if (this.membersPickerModal) {
            return this.membersPickerModal;
        }

        return (this.membersPickerModal = this.buildMemberPickerModal());
    }

    private buildMemberPickerModal() {
        const modal = new MemberPickerModalComponent(this.memberCardIds.map(id => {
            const card = this.memberCardsMap[id].clone(guid());
            
            card.outlineOnChecked = true;
            card.disabled = false;

            $(card.element)
                .toggleClass("search-filter__member", false)
                .toggleClass("search-filter__member--hidden", false)
				.toggleClass("member-picker-modal__member-card", true)
                .toggleClass("member-card-checkbox--has-remove-button", false)
				.find('.member-card__remove-button').detach();

            return card;
        }));

        modal.onMemberChange$.subscribe((_, memberChange) => {
            this.setMemberCardChecked(memberChange.key, memberChange.checked);
            this.updateSearchWarning();
        });

        return modal;
    }

    private setMemberCardChecked(id: number, state: boolean) {
        const card = this.memberCardsMap[id];
        card.checked = state;

        this.updateSelectedAllMemberCardState();
    }

    private buildSelectedAllMembersCard() {
        this.selectedAllMembersCard = new NumberCardComponent(0, "");
		this.selectedAllMembersCard.setAll();

		var card = $(this.selectedAllMembersCard.render()).toggleClass("search-filter__member number-card--default", true);

		$(".search-filter__members", this.formElement).prepend(card);
		$(this.selectedAllMembersCard.element!).find(".number-card__details").remove();
		$(this.selectedAllMembersCard.element!).find(".number-card__remove-button").remove();
        this.setNumberCardVisibility(this.selectedAllMembersCard, false);
    }

    private updateSelectedAllMemberCardState() {
        const checkedMembersCount = this.memberCards.filter(c => c.checked).length;
        this.setNumberCardVisibility(this.selectedAllMembersCard, checkedMembersCount === 0);
    }

    //#endregion

    //#region Allowance types modal
    private buildAllowanceTypePickerModal() {
        const $accordion = $(".allowance-type-accordion", this.formElement).detach();

        const $itemHeaders = $accordion.find(".accordion__header");

        const allowanceTypeAccordionHeaderAndContents = $itemHeaders.next().addBack().detach().toArray().reduce((headerContentPairs, _, index, array) => {
            if (index % 2 === 0) {
                headerContentPairs.push({ header: array[index], content: array[index + 1] });
            }

            return headerContentPairs;
        }, [] as { header: HTMLElement, content: HTMLElement }[]);

        this.allowanceTypePickerModal = new AllowanceTypePickerModalComponent(allowanceTypeAccordionHeaderAndContents.map(pair => {
            return new AllowanceTypeAccordionItemComponent(pair.header, pair.content);
        }));

        this.allowanceTypePickerModal.onExpenditureTypeChange$.subscribe(this.onExpenditureTypeChange);
    }
    
    private buildAllowanceTypeCards() {
        const allowanceTypeCards = this.allowanceTypePickerModal.allowanceTypes.map(a => new NumberCardComponent(a.id, a.name));
        this.allowanceTypeCardsMap = toDictionary(allowanceTypeCards, a => a.id);
        this.allowanceTypeCardIds = allowanceTypeCards.map(a => a.id);

        for (const card of this.allowanceTypeCards) {
			$(this.allowanceTypeCardContainer).append(card.render());
			var $cardElement = $(card.element!);
			$cardElement.addClass("search-filter__allowance-type");

			var cardName = $cardElement.find(".number-card__name").text();

			var $removebutton = $cardElement.find(".number-card__remove-button");
			$removebutton.text($removebutton.data("remove-text").replace('{0}', cardName));
			$removebutton.removeAttr('data-remove-text');

			this.updateAllowanceTypeCardState(card);
			card.onclick$.subscribe(() => {
				this.allowanceTypePickerModal.allowanceTypeMap[card.id].setAllChecked(false);
			});
        }

        this.updateSelectedAllAllowanceTypesCardState();
    }

    private updateAllowanceTypeCardState(card: NumberCardComponent) {
        const count = this.allowanceTypePickerModal.allowanceTypeMap[card.id].selectedExpenditureTypes.length;
		this.setNumberCardVisibility(card, count > 0);

        if (this.allowanceTypePickerModal.allowanceTypeMap[card.id].expenditureTypes.length === count) {
            card.setAll();
        } else {
            card.setNumber(count);
        }
    }

    private buildSelectedAllAllowanceTypesCard() {
        this.selectedAllAllowanceTypesCard = new NumberCardComponent(0, "");
        this.selectedAllAllowanceTypesCard.setAll();

		$(".search-filter__allowance-types", this.formElement)
			.prepend($(this.selectedAllAllowanceTypesCard.render()).addClass("number-card--default"));

        $(this.selectedAllAllowanceTypesCard.element!)
            .addClass("search-filter__allowance-type")
            .find(".number-card__details").remove();
        $(this.selectedAllAllowanceTypesCard.element!).find(".number-card__remove-button").remove();
        this.setNumberCardVisibility(this.selectedAllAllowanceTypesCard, false);
    }

    private updateSelectedAllAllowanceTypesCardState() {
        const selectedAllowanceTypes = this.allowanceTypePickerModal.allowanceTypes.filter(a => a.selectedExpenditureTypes.length > 0);
        this.setNumberCardVisibility(this.selectedAllAllowanceTypesCard, selectedAllowanceTypes.length === 0);
    }

    private onAllowanceTypeChange(sender: AllowanceTypePickerModalComponent, args: ICheckboxChangeArgs<number>) {
        this.updateAllowanceTypeCardState(this.allowanceTypeCardsMap[args.key]);
        this.updateSelectedAllAllowanceTypesCardState();
    }

    private onExpenditureTypeChange(sender: AllowanceTypePickerModalComponent, args: AllowanceTypeAccordionItemExpenditureTypeChangeArgs) {
        this.updateAllowanceTypeCardState(this.allowanceTypeCardsMap[args.key]);
        this.updateSelectedAllAllowanceTypesCardState();
        this.updateSearchWarning();
    }
    //#endregion

    private isFormValid() {
        return $(this.formElement).validate().form();
    }

    private setNumberCardVisibility(card: NumberCardComponent, value: boolean) {
        $(card.element!).toggleClass("search-filter__number-card--hidden", !value);
    }
    
    public serialize(): SearchFilterModel {
        return {
            financialYear: this.financialYearElement.value,
            fromMonth: this.fromMonthElement.value,
            toMonth: this.toMonthElement.value,
            members: this.memberCardIds.map(id => this.memberCardsMap[id]).filter(card => card.checked).map(card => card.id),
            allowanceTypes: this.getEnabledAllowanceTypes()
        }
    }

	private getEnabledAllowanceTypes(): SearchFilterAllowanceTypeModel[] {
		var anySelected = false;
		var allSelectedExpenditureTypes = new Array<number>();

		var expenditureTypeObjects = this.allowanceTypePickerModal.allowanceTypes.map(a => {
            let checkedExpenditureTypes = a.expenditureTypes.filter(e => e.checked).map(e => e.id);
			if (checkedExpenditureTypes.length > 0) {
				anySelected = true;
			}

            if (checkedExpenditureTypes.length === a.expenditureTypes.length) {
                allSelectedExpenditureTypes.push(a.id);
				checkedExpenditureTypes = [];
			}

			return {
				id: a.id,
				expenditureTypes: checkedExpenditureTypes
			}
        });

        return anySelected ?
            expenditureTypeObjects.filter(x => x.expenditureTypes.length > 0 || allSelectedExpenditureTypes.includes(x.id)) :
            expenditureTypeObjects;
    }

    updateSearchWarning() {
        const currentSearchModel = this.serialize();
        if (this.lastSearchModel != null) {
            if (this.SearchFiltersAreEqual(currentSearchModel, this.lastSearchModel)) {
                this.searchWarningContainer.classList.add('search-results-warning--hidden');
            } else {
                this.searchWarningContainer.classList.remove('search-results-warning--hidden');
            }
        }
    }

    public SearchFiltersAreEqual(sf1 : SearchFilterModel, sf2 : SearchFilterModel) {
        if (sf1.financialYear !== sf2.financialYear ||
            sf1.fromMonth !== sf2.fromMonth ||
            sf1.toMonth !== sf2.toMonth ||
            sf1.members.length !== sf2.members.length ||
            sf1.allowanceTypes.length !== sf2.allowanceTypes.length) {
            return false;
        }

        if (!this.ValuesOfXExistInArrayY(sf1.members, sf2.members) || !this.ValuesOfXExistInArrayY(sf2.members, sf1.members)) {
            return false;
        }


        for (var i = 0; i < sf2.allowanceTypes.length; i++) {
            var sf2AllowanceType = sf2.allowanceTypes[i];
            if (sf1.allowanceTypes.filter(x => x.id == sf2AllowanceType.id).length !== 1) {
                return false;
            }
            var sf1AllowanceType = sf1.allowanceTypes.filter(x => x.id == sf2AllowanceType.id)[0];

            if (!this.ValuesOfXExistInArrayY(sf1AllowanceType.expenditureTypes, sf2AllowanceType.expenditureTypes) ||
                !this.ValuesOfXExistInArrayY(sf2AllowanceType.expenditureTypes, sf1AllowanceType.expenditureTypes)) {
                return false;
            }
        }

        for (var i = 0; i < sf1.allowanceTypes.length; i++) {
            var sf1AllowanceType = sf1.allowanceTypes[i];
            if (sf2.allowanceTypes.filter(x => x.id == sf1AllowanceType.id).length !== 1) {
                return false;
            }
            var sf2AllowanceType = sf2.allowanceTypes.filter(x => x.id == sf1AllowanceType.id)[0];

            if (!this.ValuesOfXExistInArrayY(sf2AllowanceType.expenditureTypes, sf1AllowanceType.expenditureTypes) ||
                !this.ValuesOfXExistInArrayY(sf1AllowanceType.expenditureTypes, sf2AllowanceType.expenditureTypes)) {
                return false;
            }
        }

        return true;
    }

    ValuesOfXExistInArrayY(arrayX: number[], arrayY: number[]) {
        for (var i = 0; i < arrayX.length; i++) {
            if (!arrayY.includes(arrayX[i])) {
                return false;
            }
        }
        return true;
    }
}

export interface SearchEventArgs {
    searchFilter: SearchFilterModel;
}